import { Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { Home } from './components/Home/Home';
import { ChooseLanguage } from './components/ChooseLanguage/ChooseLanguage';
import { PreRoleplay } from './components/PreRoleplay/PreRoleplay';
import { Navbar } from './components/Navbar/Navbar';
import { Watermark } from './components/Watermark/Watermark';
import i18n, { LanguageCodes } from './i18n/i18n';
import { ReferencesPage } from './components/StaticPages/ReferencesPage';
import { AboutPage } from './components/StaticPages/AboutPage';
import { LoginPage } from './components/LoginPage/LoginPage';

import './styles/App.scss';
import { useSettings } from './context/Settings';
import { CookiePolicyPage } from './components/StaticPages/CookiePolicyPage';
import { NotSupportedBrowser } from './components/NotSupportedBrowser/NotSupportedBrowser';

// Type guard to check if a string is a valid LanguageCode
const isLanguageCode = (lang: string): lang is LanguageCodes => {
  return (Object.values(LanguageCodes) as string[]).includes(lang);
};

// Component to handle language from URL and sync with settings
const LanguageHandler = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const { settings, saveSettings } = useSettings();

  useEffect(() => {
    if (lang && isLanguageCode(lang)) {
      if (settings.language !== lang) {
        saveSettings({ language: lang });
        i18n.changeLanguage(lang);
      }
    }
  }, [lang, settings.language, saveSettings]);

  useEffect(() => {
    if (settings.language) {
      i18n.changeLanguage(settings.language);
    }
  }, [settings.language]);

  return <>{children}</>;
};

const App = () => {
  return (
    <>
      <Suspense fallback={'loading'}>
        <NotSupportedBrowser>
          <>
            <Navbar />
            <Watermark />
            <Routes>
              {/* Root paths without language */}
              <Route path="/" element={<ChooseLanguage />} />
              <Route path="/login" element={<ChooseLanguage />} />

              {/* Routes with language parameter - use simple :lang pattern */}
              <Route
                path="/:lang/login"
                element={
                  <LanguageHandler>
                    <LoginPage />
                  </LanguageHandler>
                }
              />
              <Route
                path="/:lang/home"
                element={
                  <LanguageHandler>
                    <Home />
                  </LanguageHandler>
                }
              />
              <Route
                path="/:lang/roleplay"
                element={
                  <LanguageHandler>
                    <PreRoleplay />
                  </LanguageHandler>
                }
              />
              <Route
                path="/:lang/references"
                element={
                  <LanguageHandler>
                    <ReferencesPage />
                  </LanguageHandler>
                }
              />
              <Route
                path="/:lang/about"
                element={
                  <LanguageHandler>
                    <AboutPage />
                  </LanguageHandler>
                }
              />
              <Route
                path="/:lang/cookie-policy"
                element={
                  <LanguageHandler>
                    <CookiePolicyPage />
                  </LanguageHandler>
                }
              />

              {/* Fallback routes - redirect to language selection if no language in URL */}
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/roleplay" element={<Navigate to="/" replace />} />
              <Route path="/references" element={<Navigate to="/" replace />} />
              <Route path="/about" element={<Navigate to="/" replace />} />
              <Route path="/cookie-policy" element={<Navigate to="/" replace />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </>
        </NotSupportedBrowser>
      </Suspense>
    </>
  );
};

export default App;
